import { nativeService } from '@bluecodecom/native';

import getStringifiedObject from '@bluecodecom/utils/getStringifiedObject';

/** Only the field this module reads — the payload carries the whole VAS host config. */
type HostConfigPayload = { sdkSessionId?: string | null };

let installed = false;

/**
 * Ask the SDK host for its telemetry session id and hand each answer to
 * `onSessionId`, in any webview the SDK hosts.
 *
 * ## Why this is in the tracking service rather than in a component
 *
 * `session_id` is what joins a webview's Mixpanel events to BigQuery
 * `telemetry_events_v1` rows and to server-side events, and the catalog wants
 * it on *every* event. It used to arrive through `<SdkSessionSuperProperty />`,
 * a component reading `NativeProviders.VasHostConfig` — which meant the
 * property existed in exactly the four minis that happened to mount both, and
 * three more minis mounted the provider while never registering the property
 * at all. That is per-app bookkeeping nobody is reminded of, and it had already
 * been got wrong three times out of seven (NAT-151). Doing it at init makes it
 * a property of the tracking service instead of a thing each app remembers.
 *
 * ## What it does and does not assume
 *
 * The handler is *chained* onto whatever already owns
 * `window.setVasHostConfig` (and `VasHostConfigProvider` chains the same way),
 * so provider and service coexist in either mount order rather than the later
 * one silently winning.
 *
 * Guarded on {@link nativeService.isNativeEnvironment} so a browser build —
 * sdk-selector, invite, merchant-sdk-launcher, virtual-terminal — does not ask
 * a bridge that is not there. Inside a native webview the request is
 * best-effort by design: `runJsFunction` logs and returns when the host does
 * not implement it, so a host that never answers leaves `session_id` absent,
 * exactly as today. Verified for the mini container, which is where the
 * mechanism was built; the non-mini containers (settings, terms, bank-search,
 * the VAS webview itself) are unverified on a device and can only degrade to
 * that same silence.
 *
 * Idempotent: repeated `init` calls install one handler.
 */
export const subscribeToSdkSession = (
  onSessionId: (sdkSessionId: string | null | undefined) => void,
) => {
  if (installed || !nativeService.isNativeEnvironment()) {
    return;
  }

  installed = true;

  const previous = window.setVasHostConfig;

  window.setVasHostConfig = (payload: string) => {
    previous?.(payload);

    // The same reader `VasHostConfigProvider` uses: the parameter is typed as a
    // string but arrives as an already-parsed object from some hosts, and a
    // payload that parses to nothing yields `{}` rather than throwing. Losing
    // the session id costs a join key, never a rendered screen.
    const { sdkSessionId } = getStringifiedObject(payload) as HostConfigPayload;

    onSessionId(sdkSessionId);
  };

  nativeService.requestVasHostConfig();
};

/** Test seam: forget that a handler was installed. */
export const resetSdkSessionSubscription = () => {
  installed = false;
};

export default subscribeToSdkSession;
